/**
 * Utility functions for managing history points with directional filtering
 * Matches first point to origin/destination and filters data accordingly
 */

/**
 * Calculate distance between two coordinate points using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lng1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lng2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export const calculateDistanceInMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
};

/**
 * Find the first point in data that matches a location (within tolerance)
 * @param {Array} data - Array of location data points
 * @param {Object} targetLocation - Target location {lat, lng}
 * @param {number} toleranceMeters - Match tolerance in meters (default: 50)
 * @returns {Object} {index: number, point: Object} or {index: -1, point: null}
 */
export const findFirstMatchingPointIndex = (
  data,
  targetLocation,
  toleranceMeters = 50
) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { index: -1, point: null };
  }

  if (!targetLocation || typeof targetLocation.lat === "undefined") {
    return { index: -1, point: null };
  }

  for (let i = 0; i < data.length; i++) {
    const point = data[i];
    const pointLat = parseFloat(point.latitude);
    const pointLng = parseFloat(point.longitude);

    const distance = calculateDistanceInMeters(
      targetLocation.lat,
      targetLocation.lng,
      pointLat,
      pointLng
    );

    if (distance <= toleranceMeters) {
      return { index: i, point: point };
    }
  }

  return { index: -1, point: null };
};

/**
 * Filter location data based on direction (origin->destination or destination->origin)
 * @param {Array} allLocationData - All GPS location data points
 * @param {Object} originLocation - Origin stop location {lat, lng}
 * @param {Object} destinationLocation - Destination stop location {lat, lng}
 * @param {string} currentDirection - Current direction: "origin-to-destination" or "destination-to-origin"
 * @param {number} toleranceMeters - Match tolerance in meters
 * @returns {Array} Filtered location data to use for history points
 */
export const filterLocationDataByDirection = (
  allLocationData,
  originLocation,
  destinationLocation,
  currentDirection,
  toleranceMeters = 50
) => {
  if (!allLocationData || !Array.isArray(allLocationData)) {
    return [];
  }

  let matchedIndex = -1;

  if (currentDirection === "origin-to-destination") {
    // Going from origin to destination - match origin first, then use data after match
    const matchResult = findFirstMatchingPointIndex(
      allLocationData,
      originLocation,
      toleranceMeters
    );
    matchedIndex = matchResult.index;
  } else if (currentDirection === "destination-to-origin") {
    // Going from destination to origin - match destination first, then use data after match
    const matchResult = findFirstMatchingPointIndex(
      allLocationData,
      destinationLocation,
      toleranceMeters
    );
    matchedIndex = matchResult.index;
  }

  // If we found a match, return data AFTER that point
  if (matchedIndex !== -1 && matchedIndex < allLocationData.length - 1) {
    return allLocationData.slice(matchedIndex + 1);
  }

  // If no match found, return empty array
  return [];
};

/**
 * Convert location data to map waypoints for visualization
 * @param {Array} locationData - Location data points
 * @returns {Array} Array of {lat, lng} objects
 */
export const convertToWaypoints = (locationData) => {
  if (!locationData || !Array.isArray(locationData)) {
    return [];
  }

  return locationData
    .map((point) => ({
      lat: parseFloat(point.latitude),
      lng: parseFloat(point.longitude),
    }))
    .filter((point) => !isNaN(point.lat) && !isNaN(point.lng));
};

/**
 * Save history points to localStorage for persistence across refreshes
 * @param {Array} waypoints - Waypoints to save
 * @param {string} vehicleId - Vehicle ID for unique key
 */
export const saveHistoryPointsToLocalStorage = (waypoints, vehicleId) => {
  if (!vehicleId) return;

  try {
    const key = `historyPoints_${vehicleId}`;
    const data = {
      waypoints: waypoints,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving history points to localStorage:", error);
  }
};

/**
 * Load history points from localStorage
 * @param {string} vehicleId - Vehicle ID to retrieve data for
 * @returns {Array} Saved waypoints or empty array
 */
export const loadHistoryPointsFromLocalStorage = (vehicleId) => {
  if (!vehicleId) return [];

  try {
    const key = `historyPoints_${vehicleId}`;
    const data = localStorage.getItem(key);

    if (data) {
      const parsed = JSON.parse(data);
      return parsed.waypoints || [];
    }
  } catch (error) {
    console.error("Error loading history points from localStorage:", error);
  }

  return [];
};

/**
 * Clear history points from localStorage
 * @param {string} vehicleId - Vehicle ID
 */
export const clearHistoryPointsFromLocalStorage = (vehicleId) => {
  if (!vehicleId) return;

  try {
    const key = `historyPoints_${vehicleId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing history points from localStorage:", error);
  }
};

/**
 * Merge new waypoints with existing ones, avoiding duplicates
 * @param {Array} existingWaypoints - Previously saved waypoints
 * @param {Array} newWaypoints - New waypoints to add
 * @param {number} deduplicationToleranceMeters - Tolerance for duplicate detection
 * @returns {Array} Merged waypoints
 */
export const mergeWaypoints = (
  existingWaypoints,
  newWaypoints,
  deduplicationToleranceMeters = 30
) => {
  if (!newWaypoints || !Array.isArray(newWaypoints)) {
    return existingWaypoints || [];
  }

  const merged = [...(existingWaypoints || [])];

  for (const newPoint of newWaypoints) {
    let isDuplicate = false;

    // Check if this point is too close to existing points
    for (const existingPoint of merged) {
      const distance = calculateDistanceInMeters(
        existingPoint.lat,
        existingPoint.lng,
        newPoint.lat,
        newPoint.lng
      );

      if (distance < deduplicationToleranceMeters) {
        isDuplicate = true;
        break;
      }
    }

    // Add if not a duplicate
    if (!isDuplicate) {
      merged.push(newPoint);
    }
  }

  return merged;
};
